import React, { useMemo, useState } from 'react';
import { Avatar, Box, Stack, Typography } from '@mui/joy';
import Markdown from 'marked-react';
import SmartToyOutlinedIcon from '@mui/icons-material/SmartToyOutlined';
import { formatLogTimestamp } from '../../../helpers/utils';
import { setMessageFeedback } from '../../../helpers/agents';
import TextSelectionPopover from '../../../components/TextSelectionPopover';
import MessageActions from '../components/MessageActions';
import DocumentChip from '../../../components/DocumentChip';
import { parseMessageContent, PolicyChip, TaskChip } from './mentionUtils';

const AssistantMessage = ({ message, agentId, onQuote, onOpenDocument, onOpenPolicy, onOpenTask }) => {
  const [currentFeedback, setCurrentFeedback] = useState(message.feedback || null);
  const [feedbackLoading, setFeedbackLoading] = useState(false);

  const contentSegments = useMemo(() => parseMessageContent(message.content), [message.content]);
  const hasMentions = useMemo(
    () => contentSegments.some((s) => s.type !== 'text'),
    [contentSegments]
  );

  const inlineRenderer = useMemo(() => ({
    paragraph: (children) => <span style={{ display: 'inline' }}>{children}</span>,
  }), []);

  const renderContent = () => {
    if (!hasMentions) return <Markdown>{message.content}</Markdown>;

    return contentSegments.map((segment, idx) => {
      if (segment.type === 'text') return <Markdown key={idx} renderer={inlineRenderer}>{segment.content}</Markdown>;
      if (segment.type === 'document') {
        return (
          <DocumentChip
            key={idx}
            document={{ name: segment.fileName, path: segment.filePath }}
            onClick={() => onOpenDocument?.({ id: segment.filePath, name: segment.fileName, path: segment.filePath, type: segment.fileName?.split('.').pop()?.toLowerCase() })}
            size="sm"
            showOpenIcon={false}
          />
        );
      }
      if (segment.type === 'policy') {
        return <PolicyChip key={idx} policyId={segment.policyId} fieldKey={segment.fieldKey} onClick={() => onOpenPolicy?.(segment.policyId, segment.fieldKey)} />;
      }
      if (segment.type === 'task') {
        return <TaskChip key={idx} taskId={segment.taskId} onClick={() => onOpenTask?.(segment.taskId)} />;
      }
      return null;
    });
  };

  const handleFeedback = async (value) => {
    if (!message.id || !agentId) return;

    const newValue = currentFeedback === value ? null : value;
    setCurrentFeedback(newValue);
    setFeedbackLoading(true);

    try {
      await setMessageFeedback(agentId, message.id, newValue);
    } catch {
      setCurrentFeedback(message.feedback || null);
    } finally {
      setFeedbackLoading(false);
    }
  };

  return (
    <Stack
      direction="row"
      gap={2}
      className="message-container"
      sx={{
        mb: 3,
        alignItems: 'flex-start',
      }}
    >
      {/* AI Avatar */}
      <Avatar
        size="sm"
        sx={{
          background: (theme) =>
            `linear-gradient(135deg, ${theme.vars.palette.primary[400]} 0%, ${theme.vars.palette.primary[600]} 100%)`,
          color: 'white',
          flexShrink: 0,
          boxShadow: 'sm',
        }}
      >
        <SmartToyOutlinedIcon sx={{ fontSize: 20 }} />
      </Avatar>

      {/* Message Content */}
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <TextSelectionPopover source="assistant" onQuote={onQuote}>
          <Box
            sx={{
              // Markdown styling
              '& p': {
                m: 0,
                mb: 1.5,
                '&:last-child': { mb: 0 },
                lineHeight: 1.7,
              },
              '& strong': {
                fontWeight: 600,
              },
              '& em': {
                fontStyle: 'italic',
              },
              '& ul, & ol': {
                m: 0,
                mb: 1.5,
                pl: 2.5,
                '&:last-child': { mb: 0 },
              },
              '& li': {
                mb: 0.5,
                lineHeight: 1.6,
                '&:last-child': { mb: 0 },
              },
              '& code': {
                bgcolor: (theme) =>
                  theme.palette.mode === 'dark' ? 'neutral.800' : 'neutral.100',
                px: 0.75,
                py: 0.25,
                borderRadius: 'xs',
                fontFamily: 'monospace',
                fontSize: '0.875em',
              },
              '& pre': {
                bgcolor: (theme) =>
                  theme.palette.mode === 'dark' ? 'neutral.800' : 'neutral.900',
                color: 'neutral.50',
                p: 2,
                borderRadius: 'md',
                overflow: 'auto',
                mb: 1.5,
                '&:last-child': { mb: 0 },
                '& code': {
                  bgcolor: 'transparent',
                  color: 'inherit',
                  p: 0,
                },
              },
              '& blockquote': {
                borderLeft: '3px solid',
                borderColor: 'neutral.300',
                pl: 2,
                ml: 0,
                mr: 0,
                my: 1.5,
                color: 'text.secondary',
                fontStyle: 'italic',
              },
              '& h1, & h2, & h3, & h4, & h5, & h6': {
                mt: 2,
                mb: 1,
                fontWeight: 600,
                lineHeight: 1.3,
                '&:first-child': { mt: 0 },
              },
              '& h1': { fontSize: '1.5rem' },
              '& h2': { fontSize: '1.25rem' },
              '& h3': { fontSize: '1.125rem' },
              '& a': {
                color: 'primary.600',
                textDecoration: 'underline',
                textUnderlineOffset: '2px',
                '&:hover': {
                  color: 'primary.700',
                },
              },
              '& table': {
                width: '100%',
                borderCollapse: 'collapse',
                mb: 1.5,
                fontSize: '0.875rem',
              },
              '& th, & td': {
                border: '1px solid',
                borderColor: 'neutral.200',
                px: 1.5,
                py: 1,
                textAlign: 'left',
              },
              '& th': {
                bgcolor: 'neutral.50',
                fontWeight: 600,
              },
              '& hr': {
                border: 'none',
                borderTop: '1px solid',
                borderColor: 'neutral.200',
                my: 2,
              },
              fontSize: 'var(--joy-fontSize-md)',
              lineHeight: 'var(--joy-lineHeight-md)',
              wordBreak: 'break-word',
              color: 'text.primary',
              '& .MuiChip-root': {
                verticalAlign: 'middle',
                mx: 0.5,
              },
            }}
          >
            {renderContent()}
          </Box>
        </TextSelectionPopover>

        {/* Footer: Timestamp and Actions */}
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          sx={{ mt: 1.5 }}
        >
          {message.timestamp && (
            <Typography level="body-xs" color="neutral">
              {formatLogTimestamp(message.timestamp)}
            </Typography>
          )}
          
          <MessageActions
            content={message.content}
            messageId={message.id}
            feedback={currentFeedback}
            onFeedback={handleFeedback}
            feedbackLoading={feedbackLoading}
            showFeedback={!!message.id}
          />
        </Stack>
      </Box>
    </Stack>
  );
};

export default AssistantMessage;
